import { SmartTableColumn } from '@toggle/design-system';
import { generatePath } from '@toggle/helpers';
import { Entity, formatTicker } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import { AssetLogoContainer } from '~/components/asset-logo-container/AssetLogoContainer';
import { ErrorMessageWrapper } from '~/components/error-message-wrapper/ErrorMessageWrapper';
import { useNavigateToAssetOverview } from '~/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { SectionBase } from '~/modules/overview-knowledge-graph/components/shared/section-base/SectionBase';
import { getKnowledgeGraphSearchParams } from '~/modules/overview-knowledge-graph/utils/knowledge-graph-utils';
import { useEntities } from '~/react-query-hooks/entities/useEntities';
import { useCompanyCompetitorWithThemes } from '~/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { appPaths } from '~/routes/app-paths';
import { queryKeys } from '~/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { useCompetitorsDrawer } from '~/widgets/knowledge-graph-widget/components/competitors-tab/competitors-drawer/useCompetitorsDrawer';
import { KnowledgeGraphTabType } from '~/widgets/knowledge-graph-widget/KnowledgeGraphWidget';

import { ChangeCell } from './ChangeCell';
import { CompetitorCatalystsCell } from './CompetitorCatalystsCell';
import { CompetitorScenariosCell } from './CompetitorScenariosCell';
import styles from './RevampCompetitorsSection.module.scss';

type CompetitorTableRow = Entity & {
  priceKey?: string;
  catalystsKey?: string;
  scenariosKey?: string;
};

export interface RevampCompetitorsSectionProps {
  entityTag: string;
  isInDrawer?: boolean;
}

export const RevampCompetitorsSection = ({
  entityTag,
  isInDrawer = false,
}: RevampCompetitorsSectionProps) => {
  const { t } = useTranslation(['widget']);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { navigateToPage } = useNavigateToAssetOverview();

  const { openDrawer } = useCompetitorsDrawer();

  const {
    data = [],
    isError,
    isFetching,
  } = useCompanyCompetitorWithThemes({
    entityTag,
    limit: 3,
  });
  const entityTags = data.map(item => item.company);

  const {
    data: entitiesData = [],
    isError: isEntitiesError,
    isLoading: isEntitiesLoading,
  } = useEntities({
    entityTags,
    enabled: !!entityTags.length,
  });

  const knowledgeGraphSearchParams = getKnowledgeGraphSearchParams({
    entityTag,
    searchParams,
    subtab: KnowledgeGraphTabType.Competitors,
  }).toString();

  const linkToKnowledgeGraph = `${appPaths.assetOverview}?${knowledgeGraphSearchParams}`;

  const navigateToAssetOverview = (entityTag: string) => {
    navigateToPage({ entityTag });
  };

  const onClickHandler = isInDrawer
    ? (tag: string) => {
        const requestPath = generatePath({
          basePath: appPaths.assetOverview,
          params: {
            [queryKeys.assetOverviewAsset]: tag,
            [queryKeys.assetOverviewTab]: 'overview',
          },
        });
        navigate(requestPath);
      }
    : openDrawer;

  const tableColumns: SmartTableColumn<CompetitorTableRow>[] = [
    {
      label: t('widget:assetOverview.knowledgeGraphRevamp.columns.ticker'),
      key: 'ticker',
      render: (_, entity) => (
        <div className={styles.assetWrapper}>
          <AssetLogoContainer entity={entity} size={28} />
          <div className={styles.assetTextWrapper}>
            <div className={styles.titleLabel}>{entity.name_short || entity.name}</div>
            <div className={styles.subtitleLabel}>{formatTicker(entity)}</div>
          </div>
        </div>
      ),
    },
    {
      label: t('widget:assetOverview.knowledgeGraphRevamp.columns.change'),
      key: 'priceKey',
      alignment: 'right',
      render: (_, entity) => <ChangeCell entity={entity} />,
    },
    {
      label: t('widget:assetOverview.knowledgeGraphRevamp.columns.catalysts1W'),
      key: 'catalystsKey',
      render: (_, entity) => (
        <CompetitorCatalystsCell
          entityTag={entity.tag}
          source={POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW}
        />
      ),
    },
    {
      label: t('widget:assetOverview.knowledgeGraphRevamp.columns.scenarios1W'),
      key: 'scenariosKey',
      render: (_, entity) => <CompetitorScenariosCell entityTag={entity.tag} />,
    },
  ];

  if (isError || isEntitiesError) {
    return (
      <div className={styles.root}>
        <ErrorMessageWrapper displayIcon />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <SectionBase
        title={t('widget:assetOverview.knowledgeGraphRevamp.competitors')}
        isFetching={isFetching || isEntitiesLoading}
        onExpand={() => navigate(linkToKnowledgeGraph)}
        onRowClick={item =>
          isInDrawer
            ? navigateToAssetOverview(item.tag)
            : onClickHandler(item.tag)
        }
        tableData={entitiesData.map(item => ({
          ...item,
          key: item.tag,
        }))}
        tableColumns={tableColumns}
      />
    </div>
  );
};
